import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Switch } from '../../ui/switch';
import { API_URLS } from '../../../config/api';

// E-Ticaret Hosting paketleri CRUD - TR Domain paketleri mantığına benzer basit model
// DB alanları: id, name, price, original_price, period, featured (tinyint), display_order, features(json)

const defaultForm = {
  name: '',
  price: '',
  original_price: '',
  period: '/ay',
  featured: false,
  display_order: 0,
  features: [],
  url: ''
};

const AdminEcommerceHostingPackages = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [newFeature, setNewFeature] = useState('');

  const ENDPOINT = API_URLS.ECOMMERCE_HOSTING_PACKAGES;

  useEffect(() => {
    fetchList();
  }, []);

  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await fetch(ENDPOINT);
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch (e) { console.error('Non-JSON:', text); throw e; }
      const list = (data && data.data) ? data.data : [];
      setItems(list.map(normalize));
    } catch (e) {
      console.error('Fetch error', e);
    } finally {
      setLoading(false);
    }
  };

  const normalize = (p) => {
    let rawFeatures = Array.isArray(p.features) ? p.features : (p.features ? JSON.parse(p.features) : []);
    // Convert string features to object format { name: string, included: boolean }
    const normalizedFeatures = rawFeatures.map(f => {
      if (typeof f === 'string') {
        return { name: f, included: true };
      }
      return { name: f.name || f.text || '', included: f.included !== false };
    });
    
    return {
      id: p.id,
      name: p.name || p.title || '',
      price: p.price || '',
      original_price: p.original_price || p.originalPrice || '',
      period: p.period || '/ay',
      featured: p.featured == 1 || p.featured === true,
      display_order: p.display_order ?? p.displayOrder ?? 0,
      features: normalizedFeatures,
      url: p.url || ''
    };
  };

  const openCreate = () => {
    setEditing(null);
    setForm(defaultForm);
    setModalOpen(true);
  };

  const openEdit = (pkg) => {
    setEditing(pkg);
    setForm({ ...pkg });
    setModalOpen(true);
  };

  const remove = async (id) => {
    if (!confirm('Silinsin mi?')) return;
    const res = await fetch(`${ENDPOINT}?id=${id}`, { method: 'DELETE' });
    if (res.ok) fetchList();
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, featured: form.featured ? 1 : 0 };
    const method = editing ? 'PUT' : 'POST';
    const url = editing ? `${ENDPOINT}?id=${editing.id}` : ENDPOINT;
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (res.ok) {
      setModalOpen(false);
      setEditing(null);
      setForm(defaultForm);
      fetchList();
    }
  };

  const addFeature = () => {
    if (!newFeature.trim()) return;
    setForm((f) => ({ 
      ...f, 
      features: [...(f.features || []), { name: newFeature.trim(), included: true }] 
    }));
    setNewFeature('');
  };
  
  const toggleFeatureIncluded = (idx) => {
    setForm((f) => {
      const updatedFeatures = [...(f.features || [])];
      if (updatedFeatures[idx]) {
        updatedFeatures[idx] = {
          ...updatedFeatures[idx],
          included: !updatedFeatures[idx].included
        };
      }
      return { ...f, features: updatedFeatures };
    });
  };

  const removeFeature = (idx) => {
    setForm((f) => ({ ...f, features: (f.features || []).filter((_, i) => i !== idx) }));
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">E-Ticaret Hosting Paketleri</h1>
        <button onClick={openCreate} className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Yeni Paket
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.sort((a,b) => (a.display_order||0) - (b.display_order||0)).map((pkg) => (
            <div key={pkg.id} className="bg-white rounded-lg shadow">
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">{pkg.name}</h3>
                    {pkg.featured ? (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">Öne Çıkan</span>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEdit(pkg)} className="p-2 text-purple-600 hover:bg-purple-50 rounded">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(pkg.id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1">{pkg.price}</div>
                {pkg.original_price && (
                  <div className="text-sm text-gray-500 line-through">{pkg.original_price}</div>
                )}
                <div className="text-sm text-gray-600 mb-3">{pkg.period}</div>
                <ul className="text-sm space-y-1 mb-4">
                  {(pkg.features||[]).slice(0,4).map((f,i) => {
                    const featureName = f.name || f;
                    const isIncluded = typeof f === 'object' ? (f.included !== false) : true;
                    return (
                      <li key={i} className={isIncluded ? 'text-gray-700' : 'text-gray-400 line-through'}>
                        • {featureName}
                      </li>
                    );
                  })}
                </ul>
                <div className="text-xs text-gray-500">Sıra: {pkg.display_order || 0}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-[95vw] sm:max-w-md md:max-w-2xl max-h-[90vh] overflow-y-auto my-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base sm:text-xl font-bold break-words">{editing ? 'Paketi Düzenle' : 'Yeni Paket'}</h2>
              <button onClick={() => { setModalOpen(false); setEditing(null); setForm(defaultForm); }} className="text-gray-500 hover:text-gray-700 flex-shrink-0">
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <form onSubmit={save} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ad</label>
                  <input className="w-full border rounded px-3 py-2" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sıra</label>
                  <input type="number" className="w-full border rounded px-3 py-2" value={form.display_order||0} onChange={(e)=>setForm({...form, display_order: Number(e.target.value||0)})} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fiyat</label>
                  <input className="w-full border rounded px-3 py-2" value={form.price} onChange={(e)=>setForm({...form, price:e.target.value})} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Orijinal Fiyat</label>
                  <input className="w-full border rounded px-3 py-2" value={form.original_price} onChange={(e)=>setForm({...form, original_price:e.target.value})} />
                </div>
                <div className="sm:col-span-2 lg:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Periyot</label>
                  <input className="w-full border rounded px-3 py-2" value={form.period} onChange={(e)=>setForm({...form, period:e.target.value})} />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input id="featured" type="checkbox" checked={!!form.featured} onChange={(e)=>setForm({...form, featured:e.target.checked})} className="flex-shrink-0" />
                <label htmlFor="featured" className="text-sm">Öne Çıkan</label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL (Paket Sayfası Linki)</label>
                <input 
                  className="w-full border rounded px-3 py-2" 
                  type="text" 
                  placeholder="/eticaret-hosting veya https://example.com/paket"
                  value={form.url || ''} 
                  onChange={(e)=>setForm({...form, url:e.target.value})} 
                />
                <p className="text-xs text-gray-500 mt-1 break-words">Paket detay sayfası veya satın alma sayfası URL'si</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Özellikler</label>
                <div className="flex flex-col sm:flex-row gap-2 mb-2">
                  <input className="flex-1 border rounded px-3 py-2 min-w-0" value={newFeature} onChange={(e)=>setNewFeature(e.target.value)} placeholder="Özellik ekle" />
                  <button type="button" onClick={addFeature} className="px-3 py-2 bg-gray-100 rounded hover:bg-gray-200 whitespace-nowrap flex-shrink-0">Ekle</button>
                </div>
                <div className="space-y-2">
                  {(form.features||[]).map((f,i)=> {
                    const featureName = f.name || f;
                    const isIncluded = typeof f === 'object' ? (f.included !== false) : true;
                    return (
                      <div key={i} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 border rounded px-3 py-2 bg-gray-50">
                        <div className="flex items-center gap-2 sm:gap-3 flex-1 w-full sm:w-auto min-w-0">
                          <Switch 
                            checked={isIncluded}
                            onCheckedChange={() => toggleFeatureIncluded(i)}
                            className="data-[state=checked]:bg-green-600 flex-shrink-0"
                          />
                          <span className={`text-sm flex-1 min-w-0 break-words ${isIncluded ? 'text-gray-800 font-medium' : 'text-gray-400 line-through'}`}>
                            {featureName}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0 ${isIncluded ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {isIncluded ? 'Dahil' : 'Hariç'}
                          </span>
                        </div>
                        <button type="button" onClick={()=>removeFeature(i)} className="text-red-600 hover:bg-red-50 p-1 rounded flex-shrink-0 self-end sm:self-auto">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4">
                <button type="button" onClick={()=>{ setModalOpen(false); setEditing(null); setForm(defaultForm); }} className="w-full sm:w-auto px-4 py-2 border rounded">İptal</button>
                <button type="submit" className="w-full sm:w-auto px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center justify-center gap-2">
                  <Save className="w-4 h-4" /> Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEcommerceHostingPackages;


